import { useState } from 'react';
import {
  BookOpen,
  Database,
  Shield,
  GitBranch,
  AlertTriangle,
  Download,
  Server,
  Key,
  Code2,
  Loader2,
  Link2
} from 'lucide-react';
import jsPDF from 'jspdf';

type Section =
  | 'overview'
  | 'architecture'
  | 'api'
  | 'database'
  | 'deployment'
  | 'security'
  | 'troubleshooting'
  | 'code-examples'
  | 'xtramile';

export default function Documentation() {
  const [activeSection, setActiveSection] = useState<Section>('overview');
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);

  const handleExportPDF = async () => {
    setIsGeneratingPDF(true);

    try {
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4'
      });

      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 20;
      const contentWidth = pageWidth - (margin * 2);
      let yPos = margin;

      // Helper function to check if we need a new page
      const checkNewPage = (requiredSpace: number = 10) => {
        if (yPos + requiredSpace > pageHeight - margin) {
          pdf.addPage();
          yPos = margin;
          return true;
        }
        return false;
      };

      // Helper function to add wrapped text
      const addText = (text: string, fontSize: number, isBold: boolean = false, color: number[] = [0, 0, 0]) => {
        pdf.setFontSize(fontSize);
        pdf.setFont('helvetica', isBold ? 'bold' : 'normal');
        pdf.setTextColor(color[0], color[1], color[2]);

        const lines = pdf.splitTextToSize(text, contentWidth);
        const lineHeight = fontSize * 0.5;

        lines.forEach((line: string) => {
          checkNewPage(lineHeight);
          pdf.text(line, margin, yPos);
          yPos += lineHeight;
        });
      };

      // Title Page
      pdf.setFillColor(37, 99, 235); // KF blue
      pdf.rect(0, 0, pageWidth, 50, 'F');

      pdf.setTextColor(255, 255, 255);
      pdf.setFontSize(24);
      pdf.setFont('helvetica', 'bold');
      pdf.text('API-dokumentasjon', pageWidth / 2, 25, { align: 'center' });

      pdf.setFontSize(12);
      pdf.setFont('helvetica', 'normal');
      pdf.text('Kompetanseattest Kvalitetssikring System', pageWidth / 2, 35, { align: 'center' });

      yPos = 65;

      // System Information
      addText('System: Kompetanseattest Kvalitetssikring', 14, true, [37, 99, 235]);
      yPos += 3;
      addText(`Generert: ${new Date().toLocaleDateString('nb-NO', { year: 'numeric', month: 'long', day: 'numeric' })}`, 10, false, [100, 100, 100]);
      yPos += 8;

      // Base URL Section
      addText('Base URL', 16, true, [37, 99, 235]);
      yPos += 3;
      addText('https://studieplan.kompetanseattest.no', 10);
      yPos += 8;

      // Authentication Section
      addText('Autentisering', 16, true, [37, 99, 235]);
      yPos += 3;
      addText('Alle API-kall (unntatt offentlig verifisering) krever Bearer token i Authorization header.', 10);
      yPos += 8;

      // Rate Limiting
      addText('Rate Limiting', 16, true, [37, 99, 235]);
      yPos += 3;
      addText('• Generelle endepunkter: 100 requests/minutt', 10);
      addText('• AI-endepunkter: 10 requests/minutt', 10);
      addText('• Autentisering: 5 forsøk/15 minutter', 10);
      yPos += 10;

      checkNewPage(20);

      // API Type Introduction
      addText('To typer API-tilgang', 18, true, [37, 99, 235]);
      yPos += 5;
      addText('Systemet tilbyr to separate API-er med forskjellige autentiseringsmetoder og tilgangsnivåer.', 10);
      yPos += 10;

      // =============================================================
      // EXTERNAL LMS API (API-LEGITIMASJON)
      // =============================================================
      checkNewPage(20);
      pdf.setFillColor(37, 99, 235);
      pdf.rect(margin - 5, yPos, contentWidth + 10, 12, 'F');
      pdf.setTextColor(255, 255, 255);
      pdf.setFontSize(16);
      pdf.setFont('helvetica', 'bold');
      pdf.text('API-LEGITIMASJON (EKSTERNE LMS)', margin, yPos + 8);
      pdf.setTextColor(0, 0, 0);
      yPos += 18;

      addText('For eksterne LMS-systemer. Kun prøvefunksjonalitet (2 endepunkter).', 10);
      yPos += 8;

      addText('Base URL:', 12, true);
      yPos += 3;
      addText('https://studieplan.kompetanseattest.no/api/v1', 9);
      yPos += 8;

      addText('Autentisering:', 12, true);
      yPos += 3;
      addText('X-API-Username: [ditt-api-brukernavn]', 9);
      addText('X-API-Key: [din-hemmelige-nøkkel]', 9);
      yPos += 8;

      addText('Rate Limiting: 1000 requests/time (konfigurerbart)', 10);
      yPos += 10;

      // POST /api/v1/exam/start
      checkNewPage(30);
      addText('POST /api/v1/exam/start', 13, true, [37, 99, 235]);
      yPos += 3;
      addText('Start prøvesesjon for kandidat', 9);
      yPos += 5;
      pdf.setFillColor(250, 250, 250);
      const examStartHeight = 30;
      checkNewPage(examStartHeight);
      pdf.rect(margin, yPos, contentWidth, examStartHeight, 'F');
      yPos += 5;
      pdf.setFontSize(8);
      pdf.text('Request: { "course_link": "abc123", "candidate_code": "student-001",', margin + 5, yPos);
      yPos += 4;
      pdf.text('  "extended_time_minutes": 30, "metadata": {...} }', margin + 5, yPos);
      yPos += 6;
      pdf.text('Response: { "success": true, "session_id": "uuid",', margin + 5, yPos);
      yPos += 4;
      pdf.text('  "exam_url": "https://...", "expires_at": "..." }', margin + 5, yPos);
      yPos += examStartHeight - 15;
      yPos += 10;

      // GET /api/v1/exam/result/:session_id
      checkNewPage(30);
      addText('GET /api/v1/exam/result/:session_id', 13, true, [37, 99, 235]);
      yPos += 3;
      addText('Hent prøveresultat etter levering', 9);
      yPos += 5;
      pdf.setFillColor(250, 250, 250);
      const examResultHeight = 35;
      checkNewPage(examResultHeight);
      pdf.rect(margin, yPos, contentWidth, examResultHeight, 'F');
      yPos += 5;
      pdf.setFontSize(8);
      pdf.text('URL: /api/v1/exam/result/550e8400-e29b-41d4...', margin + 5, yPos);
      yPos += 5;
      pdf.text('Response (graded): { "success": true, "status": "graded",', margin + 5, yPos);
      yPos += 4;
      pdf.text('  "score": 85, "max_score": 100, "percentage": 85,', margin + 5, yPos);
      yPos += 4;
      pdf.text('  "passed": true, "answers": [...] }', margin + 5, yPos);
      yPos += 6;
      pdf.text('Response (in_progress): { "success": true, "status": "in_progress",', margin + 5, yPos);
      yPos += 4;
      pdf.text('  "message": "Exam not yet submitted" }', margin + 5, yPos);
      yPos += examResultHeight - 23;
      yPos += 10;

      addText('HTTP Statuskoder:', 11, true);
      yPos += 4;
      addText('• 200: Suksess', 9);
      addText('• 400: Ugyldig forespørsel', 9);
      addText('• 401: Ugyldig API-legitimasjon', 9);
      addText('• 403: IP ikke hvitelistet / Org suspendert', 9);
      addText('• 404: Kurs/sesjon ikke funnet', 9);
      addText('• 409: Kandidat har allerede aktiv sesjon', 9);
      addText('• 429: Rate limit overskredet', 9);
      addText('• 500: Serverfeil', 9);
      yPos += 12;

      // =============================================================
      // INTERNAL API (BRUKERAUTENTISERING)
      // =============================================================
      checkNewPage(20);
      pdf.setFillColor(147, 51, 234); // Purple
      pdf.rect(margin - 5, yPos, contentWidth + 10, 12, 'F');
      pdf.setTextColor(255, 255, 255);
      pdf.setFontSize(16);
      pdf.setFont('helvetica', 'bold');
      pdf.text('INTERN API (BRUKERAUTENTISERING)', margin, yPos + 8);
      pdf.setTextColor(0, 0, 0);
      yPos += 18;

      addText('For påloggede brukere. Full tilgang til alle funksjoner (36 endpoints).', 10);
      yPos += 8;

      addText('Base URL:', 12, true);
      yPos += 3;
      addText('https://studieplan.kompetanseattest.no', 9);
      yPos += 8;

      addText('Autentisering:', 12, true);
      yPos += 3;
      addText('Authorization: Bearer [jwt-token-fra-innlogging]', 9);
      yPos += 8;

      addText('Rate Limiting:', 12, true);
      yPos += 3;
      addText('• Generelle: 100 req/min', 9);
      addText('• AI-endepunkter: 10 req/min', 9);
      addText('• Autentisering: 5 forsøk/15 min', 9);
      yPos += 10;

      // API Categories Overview
      addText('API Kategorier (36 endpoints):', 14, true, [37, 99, 235]);
      yPos += 5;

      const categories = [
        { name: 'Autentisering', count: 5, desc: 'Login, registrering, e-postverifisering' },
        { name: 'Studieplaner', count: 7, desc: 'CRUD-operasjoner, validering, godkjenning' },
        { name: 'Litteratur', count: 6, desc: 'Hybrid AI litteraturforslag (v1.6)' },
        { name: 'Avsluttende prøver', count: 8, desc: 'Generering, gjennomføring, tilrettelegging' },
        { name: 'Administrasjon', count: 10, desc: 'Brukere, backup, kurerte kilder' }
      ];

      categories.forEach(cat => {
        checkNewPage(15);
        addText(`${cat.name} (${cat.count} endpoints)`, 12, true);
        yPos += 1;
        addText(cat.desc, 9, false, [100, 100, 100]);
        yPos += 6;
      });

      yPos += 5;

      // Detailed Endpoints - Authentication
      checkNewPage(20);
      pdf.setFillColor(240, 240, 240);
      pdf.rect(margin, yPos, contentWidth, 10, 'F');
      addText('AUTENTISERING (5 endpoints)', 14, true, [37, 99, 235]);
      yPos += 8;

      // POST /auth/login
      checkNewPage(25);
      addText('POST /auth/login', 12, true);
      yPos += 3;
      addText('Logg inn bruker med e-post og passord.', 9);
      yPos += 5;
      addText('Request:', 10, true);
      yPos += 2;
      pdf.setFillColor(250, 250, 250);
      const loginReqHeight = 15;
      checkNewPage(loginReqHeight);
      pdf.rect(margin, yPos, contentWidth, loginReqHeight, 'F');
      yPos += 5;
      pdf.setFontSize(8);
      pdf.text('{ "email": "bruker@example.com", "password": "passord123" }', margin + 5, yPos);
      yPos += loginReqHeight;

      addText('Response: 200 OK med { "success": true, "user": {...}, "token": "..." }', 9);
      yPos += 10;

      // POST /auth/register
      checkNewPage(25);
      addText('POST /auth/register', 12, true);
      yPos += 3;
      addText('Registrer ny bruker.', 9);
      yPos += 5;
      addText('Request:', 10, true);
      yPos += 2;
      const regReqHeight = 20;
      checkNewPage(regReqHeight);
      pdf.setFillColor(250, 250, 250);
      pdf.rect(margin, yPos, contentWidth, regReqHeight, 'F');
      yPos += 5;
      pdf.setFontSize(8);
      pdf.text('{ "email": "...", "password": "...", "full_name": "...",', margin + 5, yPos);
      yPos += 4;
      pdf.text('  "study_association_id": "uuid", "role": "member" }', margin + 5, yPos);
      yPos += regReqHeight - 4;
      yPos += 10;

      // Other auth endpoints (abbreviated)
      checkNewPage(15);
      addText('POST /auth/verify-email', 12, true);
      addText('Verifiser e-postadresse med kode.', 9);
      yPos += 8;

      checkNewPage(15);
      addText('POST /auth/forgot-password', 12, true);
      addText('Send e-post for passordtilbakestilling.', 9);
      yPos += 8;

      checkNewPage(15);
      addText('POST /auth/reset-password', 12, true);
      addText('Tilbakestill passord med token.', 9);
      yPos += 12;

      // Study Plans Section
      checkNewPage(20);
      pdf.setFillColor(240, 240, 240);
      pdf.rect(margin, yPos, contentWidth, 10, 'F');
      addText('STUDIEPLANER (7 endpoints)', 14, true, [37, 99, 235]);
      yPos += 10;

      const studyPlanEndpoints = [
        { method: 'POST', path: '/api/study-plans/list', desc: 'Hent liste over studieplaner' },
        { method: 'POST', path: '/api/study-plans/get', desc: 'Hent én studieplan' },
        { method: 'POST', path: '/api/study-plans/create', desc: 'Opprett ny studieplan' },
        { method: 'POST', path: '/api/study-plans/update', desc: 'Oppdater studieplan' },
        { method: 'POST', path: '/api/study-plans/validate', desc: 'Valider med AI (GPT-4)' },
        { method: 'POST', path: '/api/study-plans/approve', desc: 'Godkjenn studieplan (admin)' },
        { method: 'POST', path: '/api/study-plans/reject', desc: 'Avvis studieplan (admin)' }
      ];

      studyPlanEndpoints.forEach(endpoint => {
        checkNewPage(12);
        addText(`${endpoint.method} ${endpoint.path}`, 11, true);
        yPos += 2;
        addText(endpoint.desc, 9);
        yPos += 7;
      });

      // Literature Section with Hybrid AI highlight
      checkNewPage(25);
      pdf.setFillColor(240, 240, 240);
      pdf.rect(margin, yPos, contentWidth, 10, 'F');
      addText('LITTERATUR (6 endpoints)', 14, true, [37, 99, 235]);
      yPos += 10;

      // Highlight box for v1.6 feature
      checkNewPage(20);
      pdf.setFillColor(219, 234, 254); // Light blue
      pdf.rect(margin, yPos, contentWidth, 18, 'F');
      yPos += 5;
      addText('✨ Hybrid AI Litteratursystem (v1.6)', 11, true, [37, 99, 235]);
      yPos += 4;
      addText('Kombinerer AI-genererte forslag med kurerte kilder for redusert hallusinasjon.', 8);
      yPos += 12;

      const literatureEndpoints = [
        { method: 'GET', path: '/api/study-plans/:id/literature', desc: 'Hent litteraturliste' },
        { method: 'POST', path: '/api/study-plans/:id/literature', desc: 'Legg til litteratur' },
        { method: 'POST', path: '/api/study-plans/:id/literature/ai-suggest', desc: 'KI-forslag (hybrid)' },
        { method: 'PUT', path: '/api/study-plans/:id/literature/:lit_id', desc: 'Oppdater litteratur' },
        { method: 'DELETE', path: '/api/study-plans/:id/literature/:lit_id', desc: 'Slett litteratur' },
        { method: 'POST', path: '/api/study-plans/:id/literature/:lit_id/download', desc: 'Last ned PDF' }
      ];

      literatureEndpoints.forEach(endpoint => {
        checkNewPage(12);
        addText(`${endpoint.method} ${endpoint.path}`, 11, true);
        yPos += 2;
        addText(endpoint.desc, 9);
        yPos += 7;
      });

      // Exams Section
      checkNewPage(20);
      pdf.setFillColor(240, 240, 240);
      pdf.rect(margin, yPos, contentWidth, 10, 'F');
      addText('AVSLUTTENDE PRØVER (8 endpoints)', 14, true, [37, 99, 235]);
      yPos += 10;

      const examEndpoints = [
        { method: 'GET', path: '/api/exams/list', desc: 'Hent tilgjengelige prøver' },
        { method: 'POST', path: '/api/exams/generate', desc: 'Generer ny prøve' },
        { method: 'POST', path: '/api/v1/exam/start', desc: 'Start prøve (anonym sesjon)' },
        { method: 'POST', path: '/api/exam/save-answer', desc: 'Lagre svar' },
        { method: 'POST', path: '/api/exam/submit', desc: 'Lever prøve' },
        { method: 'GET', path: '/api/v1/exam/result/:session_id', desc: 'Hent resultat' },
        { method: 'GET', path: '/api/accommodations/templates', desc: 'Hent tilretteleggingsmaler' },
        { method: 'POST', path: '/api/accommodations/session/:session_id', desc: 'Aktiver tilrettelegging' }
      ];

      examEndpoints.forEach(endpoint => {
        checkNewPage(12);
        addText(`${endpoint.method} ${endpoint.path}`, 11, true);
        yPos += 2;
        addText(endpoint.desc, 9);
        yPos += 7;
      });

      // Admin Section
      checkNewPage(20);
      pdf.setFillColor(240, 240, 240);
      pdf.rect(margin, yPos, contentWidth, 10, 'F');
      addText('ADMINISTRASJON (10 endpoints)', 14, true, [37, 99, 235]);
      yPos += 10;

      checkNewPage(15);
      pdf.setFillColor(254, 242, 242); // Light red
      pdf.rect(margin, yPos, contentWidth, 12, 'F');
      yPos += 4;
      addText('⚠️ Krever admin eller systemadmin rolle', 10, true, [220, 38, 38]);
      yPos += 10;

      const adminEndpoints = [
        { method: 'GET', path: '/api/admin/users', desc: 'Hent brukerliste' },
        { method: 'POST', path: '/api/admin/users/approve', desc: 'Godkjenn bruker' },
        { method: 'POST', path: '/api/admin/users/update', desc: 'Oppdater bruker' },
        { method: 'GET', path: '/api/admin/study-associations', desc: 'Hent studieforbund' },
        { method: 'GET', path: '/api/admin/backups', desc: 'List backups' },
        { method: 'POST', path: '/api/admin/backups/create', desc: 'Opprett backup' },
        { method: 'GET', path: '/api/admin/curated-sources', desc: 'Hent kurerte kilder (v1.6)' },
        { method: 'POST', path: '/api/admin/curated-sources', desc: 'Opprett kilde (v1.6)' },
        { method: 'PUT', path: '/api/admin/curated-sources/:id', desc: 'Oppdater kilde (v1.6)' },
        { method: 'DELETE', path: '/api/admin/curated-sources/:id', desc: 'Slett kilde (v1.6)' }
      ];

      adminEndpoints.forEach(endpoint => {
        checkNewPage(12);
        addText(`${endpoint.method} ${endpoint.path}`, 11, true);
        yPos += 2;
        addText(endpoint.desc, 9);
        yPos += 7;
      });

      // Error Handling Section
      checkNewPage(25);
      pdf.setFillColor(240, 240, 240);
      pdf.rect(margin, yPos, contentWidth, 10, 'F');
      addText('FEILHÅNDTERING', 14, true, [37, 99, 235]);
      yPos += 10;

      addText('Standard HTTP statuskoder:', 11, true);
      yPos += 5;
      const errorCodes = [
        '200: Suksess',
        '201: Opprettet',
        '400: Ugyldig forespørsel',
        '401: Ikke autentisert',
        '403: Ingen tilgang',
        '404: Ikke funnet',
        '429: For mange forespørsler',
        '500: Serverfeil'
      ];

      errorCodes.forEach(code => {
        checkNewPage(8);
        addText(`• ${code}`, 9);
        yPos += 5;
      });

      // Footer on each page
      const totalPages = pdf.getNumberOfPages();
      for (let i = 1; i <= totalPages; i++) {
        pdf.setPage(i);
        pdf.setFontSize(8);
        pdf.setTextColor(150, 150, 150);
        pdf.text(
          `Side ${i} av ${totalPages} • Kompetanseattest Kvalitetssikring • ${new Date().toLocaleDateString('nb-NO')}`,
          pageWidth / 2,
          pageHeight - 10,
          { align: 'center' }
        );
      }

      // Save PDF
      pdf.save(`API-dokumentasjon-${new Date().toISOString().split('T')[0]}.pdf`);

    } catch (error) {
      console.error('PDF generation error:', error);
      alert('Kunne ikke generere PDF. Vennligst prøv igjen.');
    } finally {
      setIsGeneratingPDF(false);
    }
  };

  return (
    <div className="flex gap-6">
      {/* Side Navigation */}
      <div className="w-64 flex-shrink-0">
        <div className="bg-white rounded-lg shadow-sm border p-4 sticky top-4">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <BookOpen size={20} />
              Dokumentasjon
            </h2>
          </div>
          <nav className="space-y-1">
            <NavButton
              active={activeSection === 'overview'}
              onClick={() => setActiveSection('overview')}
              icon={<BookOpen size={18} />}
            >
              Oversikt
            </NavButton>
            <NavButton
              active={activeSection === 'architecture'}
              onClick={() => setActiveSection('architecture')}
              icon={<Server size={18} />}
            >
              Systemarkitektur
            </NavButton>
            <NavButton
              active={activeSection === 'api'}
              onClick={() => setActiveSection('api')}
              icon={<Key size={18} />}
            >
              API-dokumentasjon
            </NavButton>
            <NavButton
              active={activeSection === 'database'}
              onClick={() => setActiveSection('database')}
              icon={<Database size={18} />}
            >
              Database-skjema
            </NavButton>
            <NavButton
              active={activeSection === 'code-examples'}
              onClick={() => setActiveSection('code-examples')}
              icon={<Code2 size={18} />}
            >
              Kodeeksempler
            </NavButton>
            <NavButton
              active={activeSection === 'deployment'}
              onClick={() => setActiveSection('deployment')}
              icon={<GitBranch size={18} />}
            >
              Deployment
            </NavButton>
            <NavButton
              active={activeSection === 'security'}
              onClick={() => setActiveSection('security')}
              icon={<Shield size={18} />}
            >
              Sikkerhet
            </NavButton>
            <NavButton
              active={activeSection === 'troubleshooting'}
              onClick={() => setActiveSection('troubleshooting')}
              icon={<AlertTriangle size={18} />}
            >
              Feilsøking
            </NavButton>
            <NavButton
              active={activeSection === 'xtramile'}
              onClick={() => setActiveSection('xtramile')}
              icon={<Link2 size={18} />}
            >
              Xtramile LMS
            </NavButton>
          </nav>

          <button
            onClick={handleExportPDF}
            disabled={isGeneratingPDF}
            className="w-full mt-4 btn btn-secondary flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isGeneratingPDF ? (
              <>
                <Loader2 size={18} className="animate-spin" />
                Genererer PDF...
              </>
            ) : (
              <>
                <Download size={18} />
                Eksporter PDF
              </>
            )}
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1">
        <div className="bg-white rounded-lg shadow-sm border p-8">
          {activeSection === 'overview' && <OverviewSection />}
          {activeSection === 'architecture' && <ArchitectureSection />}
          {activeSection === 'api' && <ApiDocSection />}
          {activeSection === 'database' && <DatabaseSection />}
          {activeSection === 'code-examples' && <CodeExamplesSection />}
          {activeSection === 'deployment' && <DeploymentSection />}
          {activeSection === 'security' && <SecuritySection />}
          {activeSection === 'troubleshooting' && <TroubleshootingSection />}
          {activeSection === 'xtramile' && <XtramileSection />}
        </div>
      </div>
    </div>
  );
}

function NavButton({ active, onClick, icon, children }: {
  active: boolean;
  onClick: () => void;
  icon: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      className={`w-full flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-md transition ${
        active
          ? 'bg-kf-primary text-white'
          : 'text-gray-700 hover:bg-gray-100'
      }`}
    >
      {icon}
      {children}
    </button>
  );
}

function OverviewSection() {
  return (
    <div className="prose max-w-none">
      <h1>Kompetanseattest Kvalitetssikring - Systemdokumentasjon</h1>

      <div className="bg-blue-50 border-l-4 border-blue-500 p-4 my-6">
        <div className="flex">
          <div className="flex-shrink-0">
            <BookOpen className="h-5 w-5 text-blue-500" />
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-blue-800">Velkommen til systemdokumentasjonen</h3>
            <p className="mt-2 text-sm text-blue-700">
              Denne dokumentasjonen gir en omfattende oversikt over Kompetanseattest Kvalitetssikring-systemet,
              inkludert arkitektur, API-er, database-struktur og deployment-prosedyrer.
            </p>
          </div>
        </div>
      </div>

      <h2>Om systemet</h2>
      <p>
        Kompetanseattest Kvalitetssikring er et omfattende system utviklet for Kompetanseforbundet
        (studieforbund godkjent av Kunnskapsdepartementet). Systemet validerer studieplaner mot
        EU Micro-credentials Framework og ELMv3-standarder ved hjelp av kunstig intelligens.
      </p>

      <h3>Hovedfunksjoner</h3>
      <ul>
        <li><strong>AI-basert validering:</strong> Automatisk validering av studieplaner med OpenAI GPT-4</li>
        <li><strong>Rollestyring:</strong> Systemadmin, admin (studieforbund), member (organisasjon) og sensor</li>
        <li><strong>Godkjenningsflyt:</strong> Strukturert prosess for godkjenning/avvisning med offentlig verifisering</li>
        <li><strong>Prøvehåndtering:</strong> Komplett system for prøveopprettelse, gjennomføring og sensurering</li>
        <li><strong>API-integrasjon:</strong> RESTful API for eksterne LMS-systemer</li>
        <li><strong>PDF-eksport:</strong> Generering av godkjente studieplaner som PDF</li>
        <li><strong>Sikker autentisering:</strong> Enhet-basert autentisering med verifiseringskoder</li>
      </ul>

      <h3>Teknologi-stack</h3>
      <div className="grid grid-cols-2 gap-4 my-4">
        <div className="bg-gray-50 p-4 rounded-lg">
          <h4 className="font-semibold text-kf-primary mb-2">Frontend</h4>
          <ul className="text-sm space-y-1">
            <li>React 18 + TypeScript</li>
            <li>Vite (build tool)</li>
            <li>Tailwind CSS</li>
            <li>React Router</li>
            <li>jsPDF + html2canvas</li>
          </ul>
        </div>
        <div className="bg-gray-50 p-4 rounded-lg">
          <h4 className="font-semibold text-kf-primary mb-2">Backend</h4>
          <ul className="text-sm space-y-1">
            <li>Node.js + Express</li>
            <li>PostgreSQL</li>
            <li>Socket.io</li>
            <li>Docker + Docker Compose</li>
            <li>Caddy (reverse proxy)</li>
          </ul>
        </div>
      </div>

      <h3>Systemkomponenter</h3>
      <ul>
        <li><strong>Frontend:</strong> React SPA med TypeScript</li>
        <li><strong>Email Service:</strong> Node.js backend for e-postvarsler og API</li>
        <li><strong>Database:</strong> PostgreSQL med Row Level Security (RLS)</li>
        <li><strong>Reverse Proxy:</strong> Caddy med automatisk HTTPS</li>
        <li><strong>Eksternt API:</strong> OpenAI for AI-validering</li>
      </ul>
    </div>
  );
}

function ArchitectureSection() {
  return (
    <div className="prose max-w-none">
      <h1>Systemarkitektur</h1>

      <h2>Overordnet arkitektur</h2>
      <div className="bg-gray-50 p-6 rounded-lg my-6 font-mono text-sm">
        <pre className="whitespace-pre overflow-x-auto">
{`┌─────────────────────────────────────────────────────────────┐
│                    Internet / Brukere                        │
└─────────────────────┬───────────────────────────────────────┘
                      │
                      ▼
┌─────────────────────────────────────────────────────────────┐
│              Caddy Reverse Proxy (HTTPS)                     │
│  - studieplan.kompetanseattest.no                           │
│  - Automatisk SSL/TLS (Let's Encrypt)                       │
│  - Rate limiting                                             │
└──────────┬──────────────────────────────┬───────────────────┘
           │                              │
           ▼                              ▼
┌─────────────────────┐      ┌──────────────────────────────┐
│   Frontend (Nginx)  │      │  Email Service (Node.js)     │
│   - React SPA       │      │  - Express API               │
│   - Static files    │      │  - Socket.io                 │
│   - Port 80         │      │  - SMTP integration          │
└─────────────────────┘      │  - Port 3000                 │
                             └──────────┬───────────────────┘
                                        │
                                        ▼
                             ┌──────────────────────────────┐
                             │  PostgreSQL Database         │
                             │  - User data                 │
                             │  - Study plans               │
                             │  - Exam data                 │
                             │  - RLS policies              │
                             └──────────────────────────────┘

External Services:
- OpenAI API (AI validation)
- ProISP SMTP (Email delivery)
- QR Server API (QR code generation)`}
        </pre>
      </div>

      <h2>Docker Container Struktur</h2>
      <div className="bg-gray-50 p-4 rounded-lg my-4">
        <h3 className="font-semibold mb-2">Test Environment</h3>
        <ul className="text-sm space-y-1">
          <li><code>caddy-test</code> - Reverse proxy</li>
          <li><code>kvalitetssikring-frontend-test</code> - Frontend</li>
          <li><code>kvalitetssikring-email-test</code> - Email service</li>
          <li><code>supabase-db</code> - PostgreSQL database (shared)</li>
        </ul>
      </div>

      <div className="bg-gray-50 p-4 rounded-lg my-4">
        <h3 className="font-semibold mb-2">Production Environment</h3>
        <ul className="text-sm space-y-1">
          <li><code>caddy-prod</code> - Reverse proxy</li>
          <li><code>kvalitetssikring-frontend-prod</code> - Frontend</li>
          <li><code>kvalitetssikring-email-prod</code> - Email service</li>
          <li><code>supabase-db</code> - PostgreSQL database (shared)</li>
        </ul>
      </div>

      <h2>Nettverksflyt</h2>
      <ol>
        <li><strong>Brukerforespørsel:</strong> HTTPS request til studieplan.kompetanseattest.no</li>
        <li><strong>Caddy:</strong> Terminerer SSL, ruter til frontend eller API</li>
        <li><strong>Frontend:</strong> Serverer React SPA, gjør API-kall til email service</li>
        <li><strong>Email Service:</strong> Håndterer autentisering, forretningslogikk, database-operasjoner</li>
        <li><strong>Database:</strong> PostgreSQL med RLS for sikkerhet</li>
      </ol>

      <h2>Dataflytsdiagram</h2>
      <div className="bg-gray-50 p-6 rounded-lg my-6 font-mono text-sm">
        <pre className="whitespace-pre overflow-x-auto">
{`Bruker → Frontend → Email Service → Database
   ↓                      ↓
   └─────────────────→ OpenAI API (validering)
                          ↓
                    SMTP Server (e-post)`}
        </pre>
      </div>
    </div>
  );
}

function ApiDocSection() {
  const [apiType, setApiType] = useState<'external' | 'internal'>('external');
  const [expandedSection, setExpandedSection] = useState<string>('auth');

  return (
    <div className="prose max-w-none">
      <h1>API-dokumentasjon</h1>

      <div className="bg-blue-50 border-l-4 border-blue-500 p-4 my-6">
        <div className="flex">
          <div className="flex-shrink-0">
            <Key className="h-5 w-5 text-blue-500" />
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-blue-800">To typer API-tilgang</h3>
            <p className="mt-2 text-sm text-blue-700">
              Systemet tilbyr to separate API-er med forskjellige autentiseringsmetoder og tilgangsnivåer.
            </p>
          </div>
        </div>
      </div>

      {/* API Type Selector */}
      <div className="flex gap-3 my-6 not-prose">
        <button
          onClick={() => setApiType('external')}
          className={`flex-1 px-6 py-4 rounded-lg border-2 transition text-left ${
            apiType === 'external'
              ? 'border-kf-primary bg-kf-primary/5'
              : 'border-gray-200 hover:border-gray-300'
          }`}
        >
          <div className="font-semibold text-lg mb-1">API-legitimasjon (Eksterne LMS)</div>
          <div className="text-sm text-gray-600">
            For eksterne LMS-systemer. Kun prøvefunksjonalitet (2 endepunkter).
          </div>
          <div className="text-xs text-gray-500 mt-2">
            Autentisering: X-API-Username + X-API-Key headers
          </div>
        </button>
        <button
          onClick={() => setApiType('internal')}
          className={`flex-1 px-6 py-4 rounded-lg border-2 transition text-left ${
            apiType === 'internal'
              ? 'border-kf-primary bg-kf-primary/5'
              : 'border-gray-200 hover:border-gray-300'
          }`}
        >
          <div className="font-semibold text-lg mb-1">Intern API (Brukerautentisering)</div>
          <div className="text-sm text-gray-600">
            For påloggede brukere. Full tilgang til alle funksjoner (36 endpoints).
          </div>
          <div className="text-xs text-gray-500 mt-2">
            Autentisering: Bearer token (JWT) fra innlogging
          </div>
        </button>
      </div>

      {/* External LMS API Documentation */}
      {apiType === 'external' && (
        <>
          <h2>API-legitimasjon (Eksterne LMS-systemer)</h2>

          <div className="bg-yellow-50 border-l-4 border-yellow-500 p-4 my-6">
            <div className="flex">
              <div className="flex-shrink-0">
                <Key className="h-5 w-5 text-yellow-500" />
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-yellow-800">Opprett API-legitimasjon</h3>
                <p className="mt-2 text-sm text-yellow-700">
                  API-nøkler opprettes per medlemsorganisasjon under "API-tilgang" i menyen.
                  Hver nøkkel har et brukernavn og hemmelig nøkkel for autentisering.
                </p>
              </div>
            </div>
          </div>

          <h3>Base URL</h3>
          <div className="bg-gray-50 p-3 rounded font-mono text-sm my-4">
            https://studieplan.kompetanseattest.no/api/v1
          </div>

          <h3>Autentisering</h3>
          <p>Alle API-forespørsler krever følgende HTTP headers:</p>
          <div className="bg-gray-900 text-gray-100 p-4 rounded-lg my-4">
            <pre className="text-sm">
{`X-API-Username: [ditt-api-brukernavn]
X-API-Key: [din-hemmelige-nøkkel]
Content-Type: application/json`}
            </pre>
          </div>

          <h3>Rate Limiting</h3>
          <p>API-forespørsler er begrenset til <strong>1000 requests per time</strong> per API-nøkkel (konfigurerbart).</p>

          <h3>Tilgjengelige Endpoints</h3>

          <div className="space-y-8 mt-6">
            {/* POST /api/v1/exam/start */}
            <div className="border-l-4 border-kf-primary pl-4">
              <h4 className="text-lg font-semibold text-kf-primary">POST /api/v1/exam/start</h4>
              <p className="text-sm text-gray-600 mb-4">Start en prøvesesjon for en kandidat.</p>

              <div className="bg-gray-900 text-gray-100 p-4 rounded-lg">
                <pre className="text-sm overflow-x-auto">{`// Request
{
  "course_link": "abc123def456",  // Unik kurslenke fra systemet
  "candidate_code": "student-12345",  // Studentnummer/ID fra LMS
  "extended_time_minutes": 30,  // Valgfritt: ekstra tid (tilrettelegging)
  "alternative_assessment": false,  // Valgfritt: alternativ vurdering
  "metadata": {  // Valgfritt: ekstra metadata
    "lms_course_id": "COURSE-2024-001",
    "lms_user_id": "user-789"
  }
}

// Response 200 OK
{
  "success": true,
  "session_id": "550e8400-e29b-41d4-a716-446655440000",
  "exam_url": "https://studieplan.kompetanseattest.no/take-exam/550e8400...",
  "expires_at": "2024-11-13T12:00:00Z",
  "time_limit_minutes": 120,
  "message": "Eksamen startet. Kandidaten kan nå ta eksamen via exam_url."
}

// Response 400 - Ugyldig forespørsel
{
  "success": false,
  "error": "Missing required fields",
  "message": "course_link and candidate_code are required"
}

// Response 404 - Kurs ikke funnet
{
  "success": false,
  "error": "Course not found",
  "message": "Invalid course_link or course not accessible"
}`}</pre>
              </div>

              <div className="bg-blue-50 p-3 rounded mt-3">
                <p className="text-sm text-blue-800">
                  <strong>Tips:</strong> Lagre <code>session_id</code> og <code>exam_url</code> i ditt LMS.
                  Kandidaten bruker <code>exam_url</code> for å ta prøven.
                </p>
              </div>
            </div>

            {/* GET /api/v1/exam/result/:session_id */}
            <div className="border-l-4 border-kf-primary pl-4">
              <h4 className="text-lg font-semibold text-kf-primary">GET /api/v1/exam/result/:session_id</h4>
              <p className="text-sm text-gray-600 mb-4">Hent prøveresultat etter at kandidaten har levert.</p>

              <div className="bg-gray-900 text-gray-100 p-4 rounded-lg">
                <pre className="text-sm overflow-x-auto">{`// Request (GET)
// URL: /api/v1/exam/result/550e8400-e29b-41d4-a716-446655440000
// Headers: X-API-Username, X-API-Key

// Response 200 OK - Eksamen levert og sensurert
{
  "success": true,
  "session_id": "550e8400-e29b-41d4-a716-446655440000",
  "candidate_code": "student-12345",
  "status": "graded",
  "score": 85,
  "max_score": 100,
  "percentage": 85,
  "passed": true,
  "pass_threshold": 60,
  "graded_at": "2024-11-13T11:45:00Z",
  "time_spent_minutes": 95,
  "answers": [
    {
      "question_number": 1,
      "question_text": "Forklar...",
      "answer": "Svaret her...",
      "score": 15,
      "max_score": 20,
      "feedback": "Godt svar, men..."
    }
  ]
}

// Response 200 OK - Eksamen ikke levert ennå
{
  "success": true,
  "session_id": "550e8400-e29b-41d4-a716-446655440000",
  "status": "in_progress",
  "message": "Exam not yet submitted"
}

// Response 404 - Sesjon ikke funnet
{
  "success": false,
  "error": "Session not found"
}`}</pre>
              </div>

              <div className="bg-blue-50 p-3 rounded mt-3">
                <p className="text-sm text-blue-800">
                  <strong>Tips:</strong> Poll dette endepunktet for å sjekke om kandidaten har levert prøven.
                  Status endres fra <code>in_progress</code> til <code>graded</code>.
                </p>
              </div>
            </div>
          </div>

          <h3 className="mt-8">Feilhåndtering</h3>
          <ul>
            <li><code>200</code> - Suksess</li>
            <li><code>400</code> - Ugyldig forespørsel (mangler påkrevde felt)</li>
            <li><code>401</code> - Ugyldig API-legitimasjon</li>
            <li><code>403</code> - IP-adresse ikke hvitelistet / Organisasjon suspendert</li>
            <li><code>404</code> - Kurs eller sesjon ikke funnet</li>
            <li><code>409</code> - Konflikt (kandidat har allerede aktiv sesjon)</li>
            <li><code>429</code> - Rate limit overskredet</li>
            <li><code>500</code> - Serverfeil</li>
          </ul>

          <div className="bg-green-50 border-l-4 border-green-500 p-4 my-6">
            <div className="flex">
              <div className="flex-shrink-0">
                <Key className="h-5 w-5 text-green-500" />
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-green-800">Trenger flere endpoints?</h3>
                <p className="mt-2 text-sm text-green-700">
                  Vi kan utvide API-et med flere funksjoner basert på behov fra eksterne LMS-systemer.
                  Ta kontakt for å diskutere hvilke integrasjoner som er aktuelle.
                </p>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Internal API Documentation */}
      {apiType === 'internal' && (
        <>
          <h2>Intern API (Brukerautentisering)</h2>

          <div className="bg-purple-50 border-l-4 border-purple-500 p-4 my-6">
            <div className="flex">
              <div className="flex-shrink-0">
                <Shield className="h-5 w-5 text-purple-500" />
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-purple-800">Kun for påloggede brukere</h3>
                <p className="mt-2 text-sm text-purple-700">
                  Disse endepunktene krever gyldig JWT token fra innlogging. Tilgang avhenger av brukerrolle
                  (member, admin, systemadmin).
                </p>
              </div>
            </div>
          </div>

          <h3>Base URL</h3>
          <div className="bg-gray-50 p-3 rounded font-mono text-sm my-4">
            https://studieplan.kompetanseattest.no
          </div>

          <h3>Autentisering</h3>
          <p>Alle forespørsler krever JWT token i Authorization header:</p>
          <div className="bg-gray-900 text-gray-100 p-4 rounded-lg my-4">
            <pre className="text-sm">
{`Authorization: Bearer [jwt-token-fra-innlogging]
Content-Type: application/json`}
            </pre>
          </div>

          <h3>Rate Limiting</h3>
          <p>Forskjellige grenser per endepunkt-type:</p>
          <ul>
            <li><strong>Generelle endepunkter:</strong> 100 requests/minutt</li>
            <li><strong>AI-endepunkter:</strong> 10 requests/minutt</li>
            <li><strong>Autentisering:</strong> 5 forsøk/15 minutter</li>
          </ul>

          <h3>API Endpoint Kategorier</h3>
          <div className="space-y-2 my-6">
            <ApiSectionButton
              active={expandedSection === 'auth'}
              onClick={() => setExpandedSection('auth')}
              title="Autentisering"
              count={5}
            />
            <ApiSectionButton
              active={expandedSection === 'study-plans'}
              onClick={() => setExpandedSection('study-plans')}
              title="Studieplaner"
              count={7}
            />
            <ApiSectionButton
              active={expandedSection === 'literature'}
              onClick={() => setExpandedSection('literature')}
              title="Litteratur"
              count={6}
            />
            <ApiSectionButton
              active={expandedSection === 'exams'}
              onClick={() => setExpandedSection('exams')}
              title="Avsluttende prøver"
              count={8}
            />
            <ApiSectionButton
              active={expandedSection === 'admin'}
              onClick={() => setExpandedSection('admin')}
              title="Administrasjon"
              count={10}
            />
          </div>

          {expandedSection === 'auth' && <AuthApiSection />}
          {expandedSection === 'study-plans' && <StudyPlansApiSection />}
          {expandedSection === 'literature' && <LiteratureApiSection />}
          {expandedSection === 'exams' && <ExamsApiSection />}
          {expandedSection === 'admin' && <AdminApiSection />}

          <h3 className="mt-8">Feilhåndtering</h3>
          <ul>
            <li><code>200</code> - Suksess</li>
            <li><code>201</code> - Opprettet</li>
            <li><code>400</code> - Ugyldig forespørsel</li>
            <li><code>401</code> - Ikke autentisert</li>
            <li><code>403</code> - Ingen tilgang (mangler rolle)</li>
            <li><code>404</code> - Ikke funnet</li>
            <li><code>429</code> - For mange forespørsler</li>
            <li><code>500</code> - Serverfeil</li>
          </ul>
        </>
      )}
    </div>
  );
}

function ApiSectionButton({ active, onClick, title, count }: {
  active: boolean;
  onClick: () => void;
  title: string;
  count: number;
}) {
  return (
    <button
      onClick={onClick}
      className={`w-full text-left px-4 py-3 rounded-lg border transition ${
        active
          ? 'bg-kf-primary text-white border-kf-primary'
          : 'bg-white text-gray-700 border-gray-200 hover:border-kf-primary'
      }`}
    >
      <div className="flex justify-between items-center">
        <span className="font-medium">{title}</span>
        <span className={`text-sm ${active ? 'text-white/80' : 'text-gray-500'}`}>
          {count} endpoints
        </span>
      </div>
    </button>
  );
}

function AuthApiSection() {
  return (
    <div className="space-y-8 mt-8">
      <div>
        <h3 className="text-lg font-semibold text-kf-primary">POST /auth/login</h3>
        <p className="text-sm text-gray-600 mb-4">Logger inn en bruker og returnerer JWT token.</p>
        <div className="bg-gray-900 text-gray-100 p-4 rounded-lg">
          <pre className="text-sm overflow-x-auto">{`// Request
{
  "email": "bruker@example.com",
  "password": "sikkerPassord123",
  "device_fingerprint": "unique-device-id"
}

// Response
{
  "success": true,
  "token": "jwt-token-here",
  "user": {
    "id": "uuid",
    "email": "bruker@example.com",
    "role": "member",
    "study_association_id": "uuid"
  }
}`}</pre>
        </div>
      </div>

      <div>
        <h3 className="text-lg font-semibold text-kf-primary">POST /auth/register</h3>
        <p className="text-sm text-gray-600 mb-4">Registrerer en ny bruker (krever e-postverifisering).</p>
        <div className="bg-gray-900 text-gray-100 p-4 rounded-lg">
          <pre className="text-sm overflow-x-auto">{`// Request
{
  "email": "ny@example.com",
  "password": "sikkerPassord123",
  "full_name": "Fornavn Etternavn",
  "study_association_id": "uuid",
  "role": "member"
}

// Response
{
  "success": true,
  "message": "Registrering vellykket. Sjekk e-post for verifiseringskode.",
  "user_id": "uuid"
}`}</pre>
        </div>
      </div>

      <div>
        <h3 className="text-lg font-semibold text-kf-primary">POST /auth/verify-email</h3>
        <p className="text-sm text-gray-600 mb-4">Verifiserer e-postadresse med kode.</p>
        <div className="bg-gray-900 text-gray-100 p-4 rounded-lg">
          <pre className="text-sm overflow-x-auto">{`// Request
{
  "email": "bruker@example.com",
  "code": "123456"
}

// Response
{
  "success": true,
  "message": "E-post verifisert"
}`}</pre>
        </div>
      </div>

      <div>
        <h3 className="text-lg font-semibold text-kf-primary">POST /auth/forgot-password</h3>
        <p className="text-sm text-gray-600 mb-4">Sender tilbakestillingslenke på e-post.</p>
        <div className="bg-gray-900 text-gray-100 p-4 rounded-lg">
          <pre className="text-sm overflow-x-auto">{`// Request
{
  "email": "bruker@example.com"
}

// Response
{
  "success": true,
  "message": "Tilbakestillingslenke sendt på e-post"
}`}</pre>
        </div>
      </div>

      <div>
        <h3 className="text-lg font-semibold text-kf-primary">POST /auth/reset-password</h3>
        <p className="text-sm text-gray-600 mb-4">Tilbakestiller passord med token fra e-post.</p>
        <div className="bg-gray-900 text-gray-100 p-4 rounded-lg">
          <pre className="text-sm overflow-x-auto">{`// Request
{
  "token": "reset-token-from-email",
  "new_password": "nyttSikkertPassord123"
}

// Response
{
  "success": true,
  "message": "Passord oppdatert"
}`}</pre>
        </div>
      </div>
    </div>
  );
}

function StudyPlansApiSection() {
  return (
    <div className="space-y-8 mt-8">
      <div>
        <h3 className="text-lg font-semibold text-kf-primary">POST /api/study-plans/list</h3>
        <p className="text-sm text-gray-600 mb-4">Henter liste over studieplaner. RLS filtrerer basert på brukerrolle.</p>
        <div className="bg-gray-900 text-gray-100 p-4 rounded-lg">
          <pre className="text-sm overflow-x-auto">{`// Request
{
  "user_id": "uuid",
  "status": "approved", // Valgfritt: draft, submitted, under_review, approved, rejected
  "limit": 50,
  "offset": 0
}

// Response
{
  "success": true,
  "study_plans": [
    {
      "id": "uuid",
      "title": "Studieplan tittel",
      "ects_credits": 5,
      "status": "approved",
      "created_at": "2025-11-12T10:00:00Z"
    }
  ],
  "total": 10
}`}</pre>
        </div>
      </div>

      <div>
        <h3 className="text-lg font-semibold text-kf-primary">POST /api/study-plans/get</h3>
        <p className="text-sm text-gray-600 mb-4">Henter en enkelt studieplan med full detalj.</p>
        <div className="bg-gray-900 text-gray-100 p-4 rounded-lg">
          <pre className="text-sm overflow-x-auto">{`// Request
{
  "study_plan_id": "uuid",
  "user_id": "uuid"
}

// Response
{
  "success": true,
  "study_plan": {
    "id": "uuid",
    "title": "Studieplan tittel",
    "description": "Beskrivelse",
    "ects_credits": 5,
    "estimated_hours": 125,
    "learning_outcomes": {
      "knowledge": ["Kunnskap 1", "Kunnskap 2"],
      "skills": ["Ferdighet 1"],
      "competence": ["Kompetanse 1"]
    },
    "content_modules": [
      {
        "title": "Modul 1",
        "description": "Beskrivelse",
        "hours": 40
      }
    ],
    "assessment_criteria": "Vurderingskriterier",
    "validation_score": 85,
    "status": "approved",
    "approval_link": "unique-link"
  }
}`}</pre>
        </div>
      </div>

      <div>
        <h3 className="text-lg font-semibold text-kf-primary">POST /api/study-plans/create</h3>
        <p className="text-sm text-gray-600 mb-4">Oppretter en ny studieplan.</p>
        <div className="bg-gray-900 text-gray-100 p-4 rounded-lg">
          <pre className="text-sm overflow-x-auto">{`// Request
{
  "title": "Ny studieplan",
  "description": "Beskrivelse",
  "ects_credits": 5,
  "learning_outcomes": {
    "knowledge": ["Kunnskap 1"],
    "skills": ["Ferdighet 1"],
    "competence": ["Kompetanse 1"]
  },
  "content_modules": [...],
  "assessment_criteria": "Kriterier",
  "created_by": "uuid",
  "study_association_id": "uuid"
}

// Response
{
  "success": true,
  "study_plan_id": "uuid",
  "message": "Studieplan opprettet"
}`}</pre>
        </div>
      </div>

      <div>
        <h3 className="text-lg font-semibold text-kf-primary">POST /api/study-plans/update</h3>
        <p className="text-sm text-gray-600 mb-4">Oppdaterer eksisterende studieplan.</p>
        <div className="bg-gray-900 text-gray-100 p-4 rounded-lg">
          <pre className="text-sm overflow-x-auto">{`// Request
{
  "study_plan_id": "uuid",
  "user_id": "uuid",
  "title": "Oppdatert tittel",
  "description": "Oppdatert beskrivelse",
  // ... andre felt ...
}

// Response
{
  "success": true,
  "message": "Studieplan oppdatert"
}`}</pre>
        </div>
      </div>

      <div>
        <h3 className="text-lg font-semibold text-kf-primary">POST /api/study-plans/validate</h3>
        <p className="text-sm text-gray-600 mb-4">Validerer studieplan med AI (OpenAI GPT-4).</p>
        <div className="bg-gray-900 text-gray-100 p-4 rounded-lg">
          <pre className="text-sm overflow-x-auto">{`// Request
{
  "study_plan_id": "uuid",
  "user_id": "uuid"
}

// Response
{
  "success": true,
  "validation": {
    "overall_score": 85,
    "summary": "Oppsummering av validering",
    "learning_outcomes_quality": {
      "score": 90,
      "feedback": "Tilbakemelding"
    },
    "ects_alignment": { ... },
    "improvement_suggestions": [...]
  }
}`}</pre>
        </div>
      </div>

      <div>
        <h3 className="text-lg font-semibold text-kf-primary">POST /api/study-plans/approve</h3>
        <p className="text-sm text-gray-600 mb-4">Godkjenner en studieplan (admin/systemadmin).</p>
        <div className="bg-gray-900 text-gray-100 p-4 rounded-lg">
          <pre className="text-sm overflow-x-auto">{`// Request
{
  "study_plan_id": "uuid",
  "admin_id": "uuid"
}

// Response
{
  "success": true,
  "approval_link": "https://.../verify/unique-link",
  "qr_code_url": "https://.../qr/..."
}`}</pre>
        </div>
      </div>

      <div>
        <h3 className="text-lg font-semibold text-kf-primary">POST /api/study-plans/reject</h3>
        <p className="text-sm text-gray-600 mb-4">Avviser en studieplan med grunn.</p>
        <div className="bg-gray-900 text-gray-100 p-4 rounded-lg">
          <pre className="text-sm overflow-x-auto">{`// Request
{
  "study_plan_id": "uuid",
  "admin_id": "uuid",
  "rejection_reason": "Mangler læringsutbytte"
}

// Response
{
  "success": true,
  "message": "Studieplan avvist"
}`}</pre>
        </div>
      </div>
    </div>
  );
}

function LiteratureApiSection() {
  return (
    <div className="space-y-8 mt-8">
      <div className="bg-blue-50 p-4 rounded-lg mb-6">
        <h4 className="font-semibold text-blue-800">Hybrid AI Litteratursystem (v1.6)</h4>
        <p className="text-sm text-blue-700">
          Systemet kombinerer AI-genererte forslag med kurerte kilder for å redusere hallusinasjoner.
        </p>
      </div>

      <div>
        <h3 className="text-lg font-semibold text-kf-primary">GET /api/study-plans/:id/literature</h3>
        <p className="text-sm text-gray-600 mb-4">Henter alle litteraturkilder for en studieplan.</p>
        <div className="bg-gray-900 text-gray-100 p-4 rounded-lg">
          <pre className="text-sm overflow-x-auto">{`// Response
{
  "success": true,
  "literature": [
    {
      "id": "uuid",
      "title": "Regjeringen - Nasjonal strategi",
      "url": "https://regjeringen.no/...",
      "literature_type": "webpage",
      "from_curated": true,
      "quality_level": 5
    }
  ]
}`}</pre>
        </div>
      </div>

      <div>
        <h3 className="text-lg font-semibold text-kf-primary">POST /api/study-plans/:id/literature</h3>
        <p className="text-sm text-gray-600 mb-4">Legger til manuell litteraturkilde.</p>
        <div className="bg-gray-900 text-gray-100 p-4 rounded-lg">
          <pre className="text-sm overflow-x-auto">{`// Request
{
  "title": "Bok tittel",
  "url": "https://example.com/bok.pdf",
  "description": "Beskrivelse av kilden",
  "literature_type": "pdf" // pdf, webpage, word, other
}

// Response
{
  "success": true,
  "literature_id": "uuid"
}`}</pre>
        </div>
      </div>

      <div>
        <h3 className="text-lg font-semibold text-kf-primary">POST /api/study-plans/:id/literature/ai-suggest</h3>
        <p className="text-sm text-gray-600 mb-4">Genererer KI-forslag (hybrid med kurerte kilder).</p>
        <div className="bg-gray-900 text-gray-100 p-4 rounded-lg">
          <pre className="text-sm overflow-x-auto">{`// Response
{
  "success": true,
  "suggestions": [
    {
      "title": "NDLA - Samfunnsfag",
      "url": "https://ndla.no/...",
      "description": "Beskrivelse",
      "literature_type": "webpage",
      "relevance_score": 95,
      "from_curated": true,
      "curated_source_id": "uuid"
    },
    {
      "title": "AI-generert kilde",
      "url": "https://...",
      "from_curated": false
    }
  ]
}`}</pre>
        </div>
      </div>

      <div>
        <h3 className="text-lg font-semibold text-kf-primary">PUT /api/study-plans/:id/literature/:lit_id</h3>
        <p className="text-sm text-gray-600 mb-4">Oppdaterer litteraturkilde.</p>
        <div className="bg-gray-900 text-gray-100 p-4 rounded-lg">
          <pre className="text-sm overflow-x-auto">{`// Request
{
  "title": "Oppdatert tittel",
  "description": "Oppdatert beskrivelse"
}

// Response
{
  "success": true,
  "message": "Litteratur oppdatert"
}`}</pre>
        </div>
      </div>

      <div>
        <h3 className="text-lg font-semibold text-kf-primary">DELETE /api/study-plans/:id/literature/:lit_id</h3>
        <p className="text-sm text-gray-600 mb-4">Sletter litteraturkilde (soft delete).</p>
        <div className="bg-gray-900 text-gray-100 p-4 rounded-lg">
          <pre className="text-sm overflow-x-auto">{`// Response
{
  "success": true,
  "message": "Litteratur slettet"
}`}</pre>
        </div>
      </div>

      <div>
        <h3 className="text-lg font-semibold text-kf-primary">POST /api/study-plans/:id/literature/:lit_id/download</h3>
        <p className="text-sm text-gray-600 mb-4">Laster ned og prosesserer litteraturfil (max 50MB).</p>
        <div className="bg-gray-900 text-gray-100 p-4 rounded-lg">
          <pre className="text-sm overflow-x-auto">{`// Response
{
  "success": true,
  "download_status": "downloaded",
  "local_file_path": "/path/to/file.pdf",
  "file_size": 1048576
}`}</pre>
        </div>
      </div>
    </div>
  );
}

function ExamsApiSection() {
  return (
    <div className="space-y-8 mt-8">
      <div>
        <h3 className="text-lg font-semibold text-kf-primary">GET /api/exams/list</h3>
        <p className="text-sm text-gray-600 mb-4">Henter liste over prøvesett.</p>
        <div className="bg-gray-900 text-gray-100 p-4 rounded-lg">
          <pre className="text-sm overflow-x-auto">{`// Response
{
  "success": true,
  "exam_sets": [
    {
      "id": "uuid",
      "title": "Eksamen tittel",
      "description": "Beskrivelse",
      "question_count": 20,
      "created_at": "2025-11-12T10:00:00Z"
    }
  ]
}`}</pre>
        </div>
      </div>

      <div>
        <h3 className="text-lg font-semibold text-kf-primary">POST /api/exams/generate</h3>
        <p className="text-sm text-gray-600 mb-4">Genererer prøve med AI fra studieplan.</p>
        <div className="bg-gray-900 text-gray-100 p-4 rounded-lg">
          <pre className="text-sm overflow-x-auto">{`// Request
{
  "study_plan_id": "uuid",
  "question_count": 20,
  "include_literature": true
}

// Response
{
  "success": true,
  "exam_set_id": "uuid",
  "questions": [
    {
      "id": "uuid",
      "question_text": "Spørsmål her?",
      "question_type": "multiple_choice",
      "options": [
        {"id": "a", "text": "Alternativ A"},
        {"id": "b", "text": "Alternativ B"}
      ],
      "correct_answer": "a"
    }
  ]
}`}</pre>
        </div>
      </div>

      <div>
        <h3 className="text-lg font-semibold text-kf-primary">POST /api/v1/exam/start</h3>
        <p className="text-sm text-gray-600 mb-4">Starter en ny prøvesesjon for kandidat.</p>
        <div className="bg-gray-900 text-gray-100 p-4 rounded-lg">
          <pre className="text-sm overflow-x-auto">{`// Request
{
  "exam_set_id": "uuid",
  "candidate_id": "unique-lms-id",
  "anonymous_id": "student-123"
}

// Response
{
  "success": true,
  "session_id": "uuid",
  "exam_url": "https://.../exam/session-id",
  "expires_at": "2025-11-12T14:00:00Z"
}`}</pre>
        </div>
      </div>

      <div>
        <h3 className="text-lg font-semibold text-kf-primary">POST /api/exam/save-answer</h3>
        <p className="text-sm text-gray-600 mb-4">Lagrer svar på prøvespørsmål (auto-save).</p>
        <div className="bg-gray-900 text-gray-100 p-4 rounded-lg">
          <pre className="text-sm overflow-x-auto">{`// Request
{
  "session_id": "uuid",
  "anonymous_id": "student-123",
  "question_id": "uuid",
  "answer_text": "Svar tekst", // For open-ended
  "selected_option": "a" // For multiple choice
}

// Response
{
  "success": true,
  "message": "Svar lagret"
}`}</pre>
        </div>
      </div>

      <div>
        <h3 className="text-lg font-semibold text-kf-primary">POST /api/exam/submit</h3>
        <p className="text-sm text-gray-600 mb-4">Leverer inn prøve for sensur.</p>
        <div className="bg-gray-900 text-gray-100 p-4 rounded-lg">
          <pre className="text-sm overflow-x-auto">{`// Request
{
  "session_id": "uuid",
  "anonymous_id": "student-123"
}

// Response
{
  "success": true,
  "message": "Eksamen levert",
  "status": "submitted"
}`}</pre>
        </div>
      </div>

      <div>
        <h3 className="text-lg font-semibold text-kf-primary">GET /api/v1/exam/result/:session_id</h3>
        <p className="text-sm text-gray-600 mb-4">Henter prøveresultat.</p>
        <div className="bg-gray-900 text-gray-100 p-4 rounded-lg">
          <pre className="text-sm overflow-x-auto">{`// Response
{
  "success": true,
  "result": {
    "session_id": "uuid",
    "candidate_id": "unique-id",
    "status": "completed",
    "total_questions": 20,
    "correct_answers": 17,
    "score": 85,
    "passed": true,
    "graded_at": "2025-11-12T14:30:00Z"
  }
}`}</pre>
        </div>
      </div>

      <div>
        <h3 className="text-lg font-semibold text-kf-primary">GET /api/accommodations/templates</h3>
        <p className="text-sm text-gray-600 mb-4">Henter tilgjengelige tilretteleggingsmaler.</p>
        <div className="bg-gray-900 text-gray-100 p-4 rounded-lg">
          <pre className="text-sm overflow-x-auto">{`// Response
{
  "success": true,
  "templates": [
    {
      "id": "uuid",
      "name": "Dysleksi - 50% ekstra tid",
      "accommodation_type": "extra_time",
      "default_time_multiplier": 1.5
    }
  ]
}`}</pre>
        </div>
      </div>

      <div>
        <h3 className="text-lg font-semibold text-kf-primary">POST /api/accommodations/session/:session_id</h3>
        <p className="text-sm text-gray-600 mb-4">Legger til tilrettelegging for prøvesesjon.</p>
        <div className="bg-gray-900 text-gray-100 p-4 rounded-lg">
          <pre className="text-sm overflow-x-auto">{`// Request
{
  "template_id": "uuid",
  "description": "Ekstra beskrivelse",
  "time_multiplier": 1.5
}

// Response
{
  "success": true,
  "accommodation_id": "uuid",
  "new_expires_at": "2025-11-12T15:00:00Z"
}`}</pre>
        </div>
      </div>
    </div>
  );
}

function AdminApiSection() {
  return (
    <div className="space-y-8 mt-8">
      <div className="bg-yellow-50 p-4 rounded-lg mb-6">
        <h4 className="font-semibold text-yellow-800">Administratorrettigheter påkrevd</h4>
        <p className="text-sm text-yellow-700">
          Alle endpoints i denne kategorien krever admin eller systemadmin rolle.
        </p>
      </div>

      <div>
        <h3 className="text-lg font-semibold text-kf-primary">GET /api/admin/users</h3>
        <p className="text-sm text-gray-600 mb-4">Henter liste over brukere (filtrert av RLS).</p>
        <div className="bg-gray-900 text-gray-100 p-4 rounded-lg">
          <pre className="text-sm overflow-x-auto">{`// Response
{
  "success": true,
  "users": [
    {
      "id": "uuid",
      "email": "user@example.com",
      "full_name": "Fornavn Etternavn",
      "role": "member",
      "status": "active",
      "study_association_id": "uuid"
    }
  ]
}`}</pre>
        </div>
      </div>

      <div>
        <h3 className="text-lg font-semibold text-kf-primary">POST /api/admin/users/approve</h3>
        <p className="text-sm text-gray-600 mb-4">Godkjenner en ventende bruker.</p>
        <div className="bg-gray-900 text-gray-100 p-4 rounded-lg">
          <pre className="text-sm overflow-x-auto">{`// Request
{
  "user_id": "uuid",
  "admin_id": "uuid"
}

// Response
{
  "success": true,
  "message": "Bruker godkjent"
}`}</pre>
        </div>
      </div>

      <div>
        <h3 className="text-lg font-semibold text-kf-primary">POST /api/admin/users/update</h3>
        <p className="text-sm text-gray-600 mb-4">Oppdaterer brukerdata.</p>
        <div className="bg-gray-900 text-gray-100 p-4 rounded-lg">
          <pre className="text-sm overflow-x-auto">{`// Request
{
  "user_id": "uuid",
  "full_name": "Nytt navn",
  "role": "admin" // Kun systemadmin kan endre roller
}

// Response
{
  "success": true,
  "message": "Bruker oppdatert"
}`}</pre>
        </div>
      </div>

      <div>
        <h3 className="text-lg font-semibold text-kf-primary">GET /api/admin/study-associations</h3>
        <p className="text-sm text-gray-600 mb-4">Henter studieforbund (systemadmin får alle, admin får eget).</p>
        <div className="bg-gray-900 text-gray-100 p-4 rounded-lg">
          <pre className="text-sm overflow-x-auto">{`// Response
{
  "success": true,
  "study_associations": [
    {
      "id": "uuid",
      "name": "Kompetanseforbundet",
      "organization_number": "123456789",
      "active": true
    }
  ]
}`}</pre>
        </div>
      </div>

      <div>
        <h3 className="text-lg font-semibold text-kf-primary">GET /api/admin/backups</h3>
        <p className="text-sm text-gray-600 mb-4">Henter liste over database-sikkerhetskopier (systemadmin).</p>
        <div className="bg-gray-900 text-gray-100 p-4 rounded-lg">
          <pre className="text-sm overflow-x-auto">{`// Response
{
  "success": true,
  "backups": [
    {
      "id": "uuid",
      "filename": "backup_20251112.sql",
      "size": 15728640,
      "created_at": "2025-11-12T02:00:00Z"
    }
  ]
}`}</pre>
        </div>
      </div>

      <div>
        <h3 className="text-lg font-semibold text-kf-primary">POST /api/admin/backups/create</h3>
        <p className="text-sm text-gray-600 mb-4">Oppretter ny database-sikkerhetskopi.</p>
        <div className="bg-gray-900 text-gray-100 p-4 rounded-lg">
          <pre className="text-sm overflow-x-auto">{`// Response
{
  "success": true,
  "backup_id": "uuid",
  "filename": "backup_20251112_143000.sql",
  "message": "Sikkerhetskopi opprettet"
}`}</pre>
        </div>
      </div>

      <div>
        <h3 className="text-lg font-semibold text-kf-primary">GET /api/admin/curated-sources</h3>
        <p className="text-sm text-gray-600 mb-4">Henter kurerte litteraturkilder (v1.6 - systemadmin).</p>
        <div className="bg-gray-900 text-gray-100 p-4 rounded-lg">
          <pre className="text-sm overflow-x-auto">{`// Response
{
  "success": true,
  "sources": [
    {
      "id": "uuid",
      "title": "Regjeringen.no - Nasjonal strategi",
      "url": "https://regjeringen.no/...",
      "description": "Offisiell strategi",
      "literature_type": "webpage",
      "categories": ["samfunnsfag", "politikk"],
      "quality_level": 5,
      "usage_count": 15,
      "active": true
    }
  ]
}`}</pre>
        </div>
      </div>

      <div>
        <h3 className="text-lg font-semibold text-kf-primary">POST /api/admin/curated-sources</h3>
        <p className="text-sm text-gray-600 mb-4">Oppretter ny kurert litteraturkilde.</p>
        <div className="bg-gray-900 text-gray-100 p-4 rounded-lg">
          <pre className="text-sm overflow-x-auto">{`// Request
{
  "title": "NDLA - Samfunnsfag",
  "url": "https://ndla.no/...",
  "description": "Gratis læringsressurser",
  "literature_type": "webpage",
  "categories": ["samfunnsfag", "pedagogikk"],
  "quality_level": 5
}

// Response
{
  "success": true,
  "source_id": "uuid"
}`}</pre>
        </div>
      </div>

      <div>
        <h3 className="text-lg font-semibold text-kf-primary">PUT /api/admin/curated-sources/:id</h3>
        <p className="text-sm text-gray-600 mb-4">Oppdaterer kurert litteraturkilde.</p>
        <div className="bg-gray-900 text-gray-100 p-4 rounded-lg">
          <pre className="text-sm overflow-x-auto">{`// Request
{
  "title": "Oppdatert tittel",
  "categories": ["ny-kategori"],
  "quality_level": 4
}

// Response
{
  "success": true,
  "message": "Kilde oppdatert"
}`}</pre>
        </div>
      </div>

      <div>
        <h3 className="text-lg font-semibold text-kf-primary">DELETE /api/admin/curated-sources/:id</h3>
        <p className="text-sm text-gray-600 mb-4">Sletter kurert litteraturkilde (hard delete).</p>
        <div className="bg-gray-900 text-gray-100 p-4 rounded-lg">
          <pre className="text-sm overflow-x-auto">{`// Response
{
  "success": true,
  "message": "Kilde slettet"
}`}</pre>
        </div>
      </div>
    </div>
  );
}

function DatabaseSection() {
  return (
    <div className="prose max-w-none">
      <h1>Database-skjema</h1>

      <h2>Hovedtabeller</h2>

      <div className="my-6">
        <h3 className="bg-kf-primary text-white px-4 py-2 rounded-t">study_associations</h3>
        <div className="bg-gray-50 p-4 rounded-b border border-gray-200">
          <p className="text-sm text-gray-600 mb-3">Studieforbund (organisasjoner)</p>
          <table className="min-w-full text-sm">
            <thead>
              <tr className="border-b">
                <th className="text-left py-2">Kolonne</th>
                <th className="text-left py-2">Type</th>
                <th className="text-left py-2">Beskrivelse</th>
              </tr>
            </thead>
            <tbody>
              <tr className="border-b"><td className="py-2 font-mono">id</td><td>UUID</td><td>Primary key</td></tr>
              <tr className="border-b"><td className="py-2 font-mono">name</td><td>VARCHAR(255)</td><td>Studieforbundets navn</td></tr>
              <tr className="border-b"><td className="py-2 font-mono">org_number</td><td>VARCHAR(20)</td><td>Organisasjonsnummer</td></tr>
              <tr className="border-b"><td className="py-2 font-mono">active</td><td>BOOLEAN</td><td>Om studieforbundet er aktivt</td></tr>
              <tr className="border-b"><td className="py-2 font-mono">created_at</td><td>TIMESTAMPTZ</td><td>Opprettet tidspunkt</td></tr>
            </tbody>
          </table>
        </div>
      </div>

      <div className="my-6">
        <h3 className="bg-kf-primary text-white px-4 py-2 rounded-t">members</h3>
        <div className="bg-gray-50 p-4 rounded-b border border-gray-200">
          <p className="text-sm text-gray-600 mb-3">Medlemsorganisasjoner</p>
          <table className="min-w-full text-sm">
            <thead>
              <tr className="border-b">
                <th className="text-left py-2">Kolonne</th>
                <th className="text-left py-2">Type</th>
                <th className="text-left py-2">Beskrivelse</th>
              </tr>
            </thead>
            <tbody>
              <tr className="border-b"><td className="py-2 font-mono">id</td><td>UUID</td><td>Primary key</td></tr>
              <tr className="border-b"><td className="py-2 font-mono">study_association_id</td><td>UUID</td><td>FK til study_associations</td></tr>
              <tr className="border-b"><td className="py-2 font-mono">organization_name</td><td>VARCHAR(255)</td><td>Organisasjonsnavn</td></tr>
              <tr className="border-b"><td className="py-2 font-mono">status</td><td>VARCHAR(20)</td><td>active / suspended / inactive</td></tr>
              <tr className="border-b"><td className="py-2 font-mono">ai_requests_count</td><td>INTEGER</td><td>Antall AI-forespørsler (fakturering)</td></tr>
            </tbody>
          </table>
        </div>
      </div>

      <div className="my-6">
        <h3 className="bg-kf-primary text-white px-4 py-2 rounded-t">users</h3>
        <div className="bg-gray-50 p-4 rounded-b border border-gray-200">
          <p className="text-sm text-gray-600 mb-3">Systembrukere</p>
          <table className="min-w-full text-sm">
            <thead>
              <tr className="border-b">
                <th className="text-left py-2">Kolonne</th>
                <th className="text-left py-2">Type</th>
                <th className="text-left py-2">Beskrivelse</th>
              </tr>
            </thead>
            <tbody>
              <tr className="border-b"><td className="py-2 font-mono">id</td><td>UUID</td><td>Primary key (synkronisert med auth.users)</td></tr>
              <tr className="border-b"><td className="py-2 font-mono">email</td><td>VARCHAR(255)</td><td>E-postadresse (unik)</td></tr>
              <tr className="border-b"><td className="py-2 font-mono">role</td><td>user_role</td><td>systemadmin / admin / member / sensor</td></tr>
              <tr className="border-b"><td className="py-2 font-mono">status</td><td>VARCHAR(50)</td><td>pending_approval / active / rejected</td></tr>
              <tr className="border-b"><td className="py-2 font-mono">study_association_id</td><td>UUID</td><td>FK til study_associations</td></tr>
              <tr className="border-b"><td className="py-2 font-mono">member_id</td><td>UUID</td><td>FK til members (for member/sensor)</td></tr>
            </tbody>
          </table>
        </div>
      </div>

      <h2>Row Level Security (RLS)</h2>
      <p>Alle tabeller har RLS aktivert for å sikre tilgangskontroll på database-nivå:</p>
      <ul>
        <li><strong>systemadmin:</strong> Full tilgang til alle data</li>
        <li><strong>admin:</strong> Tilgang kun til data for eget studieforbund</li>
        <li><strong>member:</strong> Tilgang til egne data og sin medlemsorganisasjon</li>
        <li><strong>sensor:</strong> Tilgang til prøver de er tildelt</li>
      </ul>
    </div>
  );
}

function CodeExamplesSection() {
  return (
    <div className="prose max-w-none">
      <h1>Kodeeksempler</h1>

      <h2>Python</h2>
      <div className="bg-gray-900 text-gray-100 p-4 rounded-lg my-4">
        <pre className="text-sm overflow-x-auto">
{`import requests
import json

# API credentials
API_USERNAME = "your-api-username"
API_KEY = "your-secret-key"
BASE_URL = "https://studieplan.kompetanseattest.no/api"

# Headers
headers = {
    "X-API-Username": API_USERNAME,
    "X-API-Key": API_KEY,
    "Content-Type": "application/json"
}

# Start exam session
def start_exam(exam_set_id, candidate_id):
    url = f"{BASE_URL}/v1/exam/start"
    payload = {
        "exam_set_id": exam_set_id,
        "candidate_id": candidate_id,
        "anonymous_id": f"student-{candidate_id}"
    }

    response = requests.post(url, headers=headers, json=payload)

    if response.status_code == 200:
        data = response.json()
        if data["success"]:
            print(f"Exam started: {data['exam_url']}")
            return data["session_id"]
    else:
        print(f"Error: {response.status_code}")
        return None

# Get exam result
def get_result(session_id):
    url = f"{BASE_URL}/v1/exam/result/{session_id}"
    response = requests.get(url, headers=headers)

    if response.status_code == 200:
        data = response.json()
        if data["success"]:
            result = data["result"]
            print(f"Score: {result['score']}%")
            print(f"Passed: {result['passed']}")
            return result
    return None

# Example usage
session_id = start_exam("exam-uuid", "12345")
if session_id:
    result = get_result(session_id)`}
        </pre>
      </div>

      <h2>JavaScript (Node.js)</h2>
      <div className="bg-gray-900 text-gray-100 p-4 rounded-lg my-4">
        <pre className="text-sm overflow-x-auto">
{`const axios = require('axios');

const API_USERNAME = 'your-api-username';
const API_KEY = 'your-secret-key';
const BASE_URL = 'https://studieplan.kompetanseattest.no/api';

// Configure axios
const api = axios.create({
  baseURL: BASE_URL,
  headers: {
    'X-API-Username': API_USERNAME,
    'X-API-Key': API_KEY,
    'Content-Type': 'application/json'
  }
});

// Start exam
async function startExam(examSetId, candidateId) {
  try {
    const response = await api.post('/v1/exam/start', {
      exam_set_id: examSetId,
      candidate_id: candidateId,
      anonymous_id: \`student-\${candidateId}\`
    });

    if (response.data.success) {
      console.log('Exam URL:', response.data.exam_url);
      return response.data.session_id;
    }
  } catch (error) {
    console.error('Error:', error.response?.data || error.message);
    return null;
  }
}

// Get result
async function getResult(sessionId) {
  try {
    const response = await api.get(\`/v1/exam/result/\${sessionId}\`);

    if (response.data.success) {
      const result = response.data.result;
      console.log(\`Score: \${result.score}%\`);
      console.log(\`Passed: \${result.passed}\`);
      return result;
    }
  } catch (error) {
    console.error('Error:', error.response?.data || error.message);
    return null;
  }
}

// Example usage
(async () => {
  const sessionId = await startExam('exam-uuid', '12345');
  if (sessionId) {
    const result = await getResult(sessionId);
  }
})();`}
        </pre>
      </div>

      <h2>PHP</h2>
      <div className="bg-gray-900 text-gray-100 p-4 rounded-lg my-4">
        <pre className="text-sm overflow-x-auto">
{`<?php

class KompetanseatestAPI {
    private $baseUrl = 'https://studieplan.kompetanseattest.no/api';
    private $username;
    private $apiKey;

    public function __construct($username, $apiKey) {
        $this->username = $username;
        $this->apiKey = $apiKey;
    }

    private function makeRequest($method, $endpoint, $data = null) {
        $ch = curl_init($this->baseUrl . $endpoint);

        $headers = [
            'X-API-Username: ' . $this->username,
            'X-API-Key: ' . $this->apiKey,
            'Content-Type: application/json'
        ];

        curl_setopt($ch, CURLOPT_HTTPHEADER, $headers);
        curl_setopt($ch, CURLOPT_RETURNTRANSFER, true);

        if ($method === 'POST') {
            curl_setopt($ch, CURLOPT_POST, true);
            curl_setopt($ch, CURLOPT_POSTFIELDS, json_encode($data));
        }

        $response = curl_exec($ch);
        $httpCode = curl_getinfo($ch, CURLINFO_HTTP_CODE);
        curl_close($ch);

        if ($httpCode === 200) {
            return json_decode($response, true);
        }

        return null;
    }

    public function startExam($examSetId, $candidateId) {
        $data = [
            'exam_set_id' => $examSetId,
            'candidate_id' => $candidateId,
            'anonymous_id' => 'student-' . $candidateId
        ];

        $result = $this->makeRequest('POST', '/v1/exam/start', $data);

        if ($result && $result['success']) {
            echo "Exam URL: " . $result['exam_url'] . "\\n";
            return $result['session_id'];
        }

        return null;
    }

    public function getResult($sessionId) {
        $result = $this->makeRequest('GET', '/v1/exam/result/' . $sessionId);

        if ($result && $result['success']) {
            $examResult = $result['result'];
            echo "Score: " . $examResult['score'] . "%\\n";
            echo "Passed: " . ($examResult['passed'] ? 'Yes' : 'No') . "\\n";
            return $examResult;
        }

        return null;
    }
}

// Example usage
$api = new KompetanseatestAPI('your-api-username', 'your-secret-key');
$sessionId = $api->startExam('exam-uuid', '12345');

if ($sessionId) {
    $result = $api->getResult($sessionId);
}

?>`}
        </pre>
      </div>
    </div>
  );
}

function DeploymentSection() {
  return (
    <div className="prose max-w-none">
      <h1>Deployment Guide</h1>

      <div className="bg-red-50 border-l-4 border-red-500 p-4 my-6">
        <div className="flex">
          <div className="flex-shrink-0">
            <AlertTriangle className="h-5 w-5 text-red-500" />
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-red-800">Viktig!</h3>
            <p className="mt-2 text-sm text-red-700">
              Deploy alltid til test-miljøet først. Test grundig før deployment til produksjon.
            </p>
          </div>
        </div>
      </div>

      <h2>Deployment-prosess</h2>
      <ol>
        <li><strong>Forberedelser</strong>
          <ul>
            <li>Sjekk at alle tester kjører</li>
            <li>Verifiser at migrations er oppdatert</li>
            <li>Oppdater CHANGELOG.md</li>
          </ul>
        </li>
        <li><strong>Test Environment</strong>
          <div className="bg-gray-900 text-gray-100 p-4 rounded-lg my-2">
            <pre className="text-sm overflow-x-auto">
{`cd /root/studieplan
npm run build
rsync -av --delete /root/studieplan/dist/ /root/studieplan-deploy/test/frontend/
docker restart kvalitetssikring-email-test`}
            </pre>
          </div>
        </li>
        <li><strong>Database Migrations</strong>
          <div className="bg-gray-900 text-gray-100 p-4 rounded-lg my-2">
            <pre className="text-sm overflow-x-auto">
{`docker exec -i supabase-db psql -U postgres -d kvalitetssikring_test \\
  < /root/studieplan/supabase/migrations/[migration-file].sql`}
            </pre>
          </div>
        </li>
        <li><strong>Testing</strong>
          <ul>
            <li>Test alle kritiske flyter</li>
            <li>Verifiser at nye funksjoner fungerer</li>
            <li>Sjekk logger for feil</li>
          </ul>
        </li>
        <li><strong>Production Deploy</strong>
          <div className="bg-gray-900 text-gray-100 p-4 rounded-lg my-2">
            <pre className="text-sm overflow-x-auto">
{`rsync -av --delete /root/studieplan/dist/ /root/studieplan-deploy/prod/frontend/
docker restart kvalitetssikring-email-prod

# Apply migrations to prod
docker exec -i supabase-db psql -U postgres -d kvalitetssikring_prod \\
  < /root/studieplan/supabase/migrations/[migration-file].sql`}
            </pre>
          </div>
        </li>
      </ol>

      <h2>Rollback-prosedyre</h2>
      <p>Hvis deployment feiler eller medfører kritiske feil:</p>
      <ol>
        <li>Reverter frontend-kode til forrige versjon</li>
        <li>Restart Docker-containere</li>
        <li>Kjør rollback-migrations hvis nødvendig</li>
        <li>Varsle brukere om nedetid hvis relevant</li>
      </ol>

      <h2>Overvåkning etter deployment</h2>
      <ul>
        <li>Overvåk feillogger i 24 timer</li>
        <li>Sjekk systemytelse og responstider</li>
        <li>Verifiser at alle integrasjoner fungerer</li>
        <li>Følg opp brukerrapporterte problemer</li>
      </ul>
    </div>
  );
}

function SecuritySection() {
  return (
    <div className="prose max-w-none">
      <h1>Sikkerhet og Best Practices</h1>

      <h2>Autentisering og Autorisasjon</h2>
      <ul>
        <li><strong>Row Level Security (RLS):</strong> Alle database-tabeller har RLS aktivert</li>
        <li><strong>JWT Tokens:</strong> 7-dagers utløpstid</li>
        <li><strong>Password Hashing:</strong> bcrypt med høy cost factor</li>
        <li><strong>2FA:</strong> Støtte for tofaktorautentisering</li>
      </ul>

      <h2>API-sikkerhet</h2>
      <ul>
        <li><strong>Rate Limiting:</strong> 1000 requests/time per API-nøkkel</li>
        <li><strong>IP Whitelisting:</strong> Valgfritt per API-nøkkel</li>
        <li><strong>HTTPS-only:</strong> All kommunikasjon over TLS</li>
        <li><strong>API Key Rotation:</strong> Mulighet for å regenerere nøkler</li>
      </ul>

      <h2>Data-sikkerhet</h2>
      <ul>
        <li><strong>Kryptering i transit:</strong> TLS 1.3</li>
        <li><strong>Kryptering at rest:</strong> PostgreSQL encryption</li>
        <li><strong>Backup:</strong> Daglige automatiske backups</li>
        <li><strong>Audit Logging:</strong> Alle kritiske operasjoner logges</li>
      </ul>

      <h2>GDPR Compliance</h2>
      <ul>
        <li>Minimal datainnsamling</li>
        <li>Brukersamtykke for e-post</li>
        <li>Rett til sletting (soft delete)</li>
        <li>Data portability (eksport til PDF/CSV)</li>
        <li>Audit trail for alle dataoperasjoner</li>
      </ul>

      <h2>Best Practices</h2>
      <div className="bg-green-50 p-4 rounded-lg my-4">
        <h3 className="text-green-800">✓ Gjør dette</h3>
        <ul className="text-sm">
          <li>Bruk miljøvariabler for hemmeligheter</li>
          <li>Roter API-nøkler regelmessig</li>
          <li>Overvåk login_attempts for mistenkelig aktivitet</li>
          <li>Hold alle dependencies oppdatert</li>
          <li>Test backups regelmessig</li>
        </ul>
      </div>

      <div className="bg-red-50 p-4 rounded-lg my-4">
        <h3 className="text-red-800">✗ Ikke gjør dette</h3>
        <ul className="text-sm">
          <li>Commit hemmeligheter til Git</li>
          <li>Bruk samme passord på tvers av miljøer</li>
          <li>Eksponer database direkte til internett</li>
          <li>Kjør som root i Docker-containere</li>
          <li>Deaktiver HTTPS i produksjon</li>
        </ul>
      </div>
    </div>
  );
}

function TroubleshootingSection() {
  return (
    <div className="prose max-w-none">
      <h1>Feilsøking</h1>

      <h2>Vanlige problemer</h2>

      <div className="border border-gray-200 rounded-lg p-4 my-4">
        <h3 className="text-lg font-semibold text-gray-900">Problem: Kan ikke logge inn</h3>
        <p className="text-sm text-gray-600 my-2">Mulige årsaker og løsninger:</p>
        <ol className="text-sm">
          <li><strong>E-post ikke bekreftet:</strong> Sjekk auth.users.confirmed_at</li>
          <li><strong>Medlemsorganisasjon suspendert:</strong> Sjekk members.status</li>
          <li><strong>RLS blokkerer:</strong> Sjekk at policies er korrekte</li>
          <li><strong>Ugyldig passord:</strong> Reset passord via admin-panel</li>
        </ol>
      </div>

      <div className="border border-gray-200 rounded-lg p-4 my-4">
        <h3 className="text-lg font-semibold text-gray-900">Problem: API returnerer 403 Forbidden</h3>
        <p className="text-sm text-gray-600 my-2">Mulige årsaker:</p>
        <ol className="text-sm">
          <li><strong>Medlemsorganisasjon inaktiv:</strong> Sjekk members.status</li>
          <li><strong>IP ikke whitelisted:</strong> Sjekk api_credentials.allowed_ips</li>
          <li><strong>API-nøkkel utløpt:</strong> Regenerer nøkkel</li>
        </ol>
      </div>

      <div className="border border-gray-200 rounded-lg p-4 my-4">
        <h3 className="text-lg font-semibold text-gray-900">Problem: Frontend lastes ikke</h3>
        <p className="text-sm text-gray-600 my-2">Feilsøkingstrinn:</p>
        <ol className="text-sm">
          <li>Sjekk at Docker-container kjører: <code>docker ps</code></li>
          <li>Sjekk Caddy-logs: <code>docker logs caddy-test</code></li>
          <li>Verifiser at frontend-filer er deployet korrekt</li>
          <li>Sjekk nettverksoppkobling fra Caddy til frontend</li>
        </ol>
      </div>

      <h2>Nyttige kommandoer</h2>
      <div className="bg-gray-900 text-gray-100 p-4 rounded-lg my-4">
        <pre className="text-sm overflow-x-auto">
{`# Sjekk status på alle containere
docker ps

# Vis logger for en service
docker logs -f kvalitetssikring-email-test

# Restart en service
docker restart kvalitetssikring-email-test

# Kjør SQL-query
docker exec -i supabase-db psql -U postgres -d kvalitetssikring_test \\
  -c "SELECT * FROM users WHERE email = 'test@example.com';"

# Sjekk database-connections
docker exec supabase-db psql -U postgres -c \\
  "SELECT count(*) FROM pg_stat_activity;"

# Backup database
docker exec supabase-db pg_dump -U postgres kvalitetssikring_test \\
  > backup_\`date +%Y%m%d\`.sql`}
        </pre>
      </div>

      <h2>Kontaktinformasjon</h2>
      <p>Ved kritiske feil eller spørsmål, kontakt:</p>
      <ul>
        <li><strong>Teknisk support:</strong> support@kompetanseforbundet.no</li>
        <li><strong>Systemadministrator:</strong> [admin@kompetanseforbundet.no]</li>
      </ul>
    </div>
  );
}

function XtramileSection() {
  return (
    <div className="prose max-w-none">
      <h1>Xtramile LMS-integrasjon</h1>

      <div className="bg-blue-50 border-l-4 border-blue-500 p-4 my-6">
        <div className="flex">
          <div className="flex-shrink-0">
            <Link2 className="h-5 w-5 text-blue-500" />
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-blue-800">Automatisk sertifikatgenerering</h3>
            <p className="mt-2 text-sm text-blue-700">
              Xtramile-integrasjonen gjør det mulig å automatisk utstede kompetanseattester
              når deltakere fullfører kurs i Xtramile LMS.
            </p>
          </div>
        </div>
      </div>

      <h2>Hvordan det fungerer</h2>
      <ol>
        <li><strong>Koble til Xtramile:</strong> Organisasjonen legger inn sin API-nøkkel fra Xtramile</li>
        <li><strong>Kursmapping:</strong> Xtramile-kurs kobles til godkjente studieplaner i systemet</li>
        <li><strong>Automatisk synkronisering:</strong> Systemet henter fullføringer fra Xtramile regelmessig</li>
        <li><strong>Sertifikatutstedelse:</strong> Kompetanseattester utstedes automatisk til kandidater</li>
      </ol>

      <div className="bg-gray-50 p-6 rounded-lg my-6 font-mono text-sm">
        <pre className="whitespace-pre overflow-x-auto">
{`┌─────────────────┐         ┌─────────────────┐         ┌─────────────────┐
│   Xtramile      │         │   Vårt system   │         │  Kandidat-      │
│   (LMS)         │         │   (Backend)     │         │  portal         │
└────────┬────────┘         └────────┬────────┘         └────────┬────────┘
         │                           │                           │
         │  GET /statistics          │                           │
         │  ?lastActivityDate=X      │                           │
         │◄──────────────────────────┤ Cron jobb                 │
         │                           │ (hver 5 min)              │
         ├──────────────────────────►│                           │
         │  [Fullførte kurs]         │                           │
         │                           │                           │
         │                           ├──► Match e-post           │
         │                           │    med profil             │
         │                           │                           │
         │                           ├──► Finn mapping           │
         │                           │    kurs→studieplan        │
         │                           │                           │
         │                           ├──► Generer                │
         │                           │    kompetanseattest       │
         │                           │                           │
         │                           ├──────────────────────────►│
         │                           │    E-post: "Ny attest"    │
         │                           │                           │`}
        </pre>
      </div>

      <h2>Oppsett steg for steg</h2>

      <h3>1. Få API-nøkkel fra Xtramile</h3>
      <p>
        Kontakt Xtramile-administrasjonen for å få utstedt en API-nøkkel for din organisasjon.
        API-nøkkelen gir tilgang til å hente kurs- og fullføringsdata.
      </p>

      <h3>2. Aktiver integrasjonen</h3>
      <ol>
        <li>Gå til <strong>Innstillinger → Xtramile LMS</strong></li>
        <li>Godta databehandlingsavtalen</li>
        <li>Lim inn API-nøkkelen</li>
        <li>Klikk "Aktiver integrasjon"</li>
      </ol>

      <h3>3. Test tilkoblingen</h3>
      <p>
        Klikk "Test tilkobling" for å verifisere at API-nøkkelen fungerer.
        Ved vellykket tilkobling vises antall kurs funnet i Xtramile.
      </p>

      <h3>4. Opprett kursmappinger</h3>
      <p>
        For hvert Xtramile-kurs som skal utstede kompetanseattester:
      </p>
      <ol>
        <li>Klikk "Legg til kursmapping"</li>
        <li>Fyll inn Xtramile kurs-ID (UUID)</li>
        <li>Velg hvilken godkjent studieplan kurset skal kobles til</li>
        <li>Lagre mappingen</li>
      </ol>

      <div className="bg-yellow-50 border-l-4 border-yellow-500 p-4 my-6">
        <div className="flex">
          <div className="flex-shrink-0">
            <AlertTriangle className="h-5 w-5 text-yellow-500" />
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-yellow-800">Viktig om kursmapping</h3>
            <p className="mt-2 text-sm text-yellow-700">
              Kun <strong>godkjente</strong> studieplaner kan kobles til Xtramile-kurs.
              Studieplanen må være fullstendig validert og godkjent av studieforbundet
              før kursmapping kan opprettes.
            </p>
          </div>
        </div>
      </div>

      <h2>Synkronisering</h2>
      <p>
        Systemet synkroniserer automatisk fullføringer fra Xtramile basert på konfigurert intervall
        (standard: hvert 5. minutt).
      </p>

      <h3>Automatisk sync</h3>
      <ul>
        <li>Kjører i bakgrunnen</li>
        <li>Henter kun nye fullføringer siden forrige synkronisering</li>
        <li>Respekterer Xtramile API rate limits</li>
      </ul>

      <h3>Manuell sync</h3>
      <p>
        Du kan også trigge manuell synkronisering ved å klikke "Synkroniser nå" i Xtramile-innstillingene.
        Dette er nyttig ved feilsøking eller for å hente data umiddelbart.
      </p>

      <h2>Kandidat-matching</h2>
      <p>
        Systemet matcher kandidater basert på e-postadresse:
      </p>
      <ul>
        <li>
          <strong>Eksisterende kandidat:</strong> Hvis e-postadressen allerede finnes i Kandidatportalen,
          utstedes kompetanseattest direkte til denne profilen.
        </li>
        <li>
          <strong>Ny kandidat:</strong> Hvis e-postadressen ikke finnes, opprettes en midlertidig profil
          og kandidaten mottar invitasjon til å registrere seg i Kandidatportalen.
        </li>
      </ul>

      <h2>Synkroniseringslogg</h2>
      <p>
        Alle synkroniseringer logges med følgende informasjon:
      </p>
      <table>
        <thead>
          <tr>
            <th>Felt</th>
            <th>Beskrivelse</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>Tidspunkt</td>
            <td>Når synkroniseringen ble utført</td>
          </tr>
          <tr>
            <td>E-post</td>
            <td>Kandidatens e-postadresse</td>
          </tr>
          <tr>
            <td>Kurs</td>
            <td>Xtramile-kurset som ble fullført</td>
          </tr>
          <tr>
            <td>Status</td>
            <td>Om kompetanseattest ble generert eller feilet</td>
          </tr>
        </tbody>
      </table>

      <h2>Feilsøking</h2>

      <div className="border border-gray-200 rounded-lg p-4 my-4">
        <h3 className="text-lg font-semibold text-gray-900">Problem: Tilkobling feiler</h3>
        <p className="text-sm text-gray-600 my-2">Mulige årsaker:</p>
        <ol className="text-sm">
          <li><strong>Feil API-nøkkel:</strong> Verifiser at nøkkelen er korrekt kopiert</li>
          <li><strong>Nøkkel utgått:</strong> Kontakt Xtramile for ny nøkkel</li>
          <li><strong>Nettverksproblem:</strong> Sjekk at serveren har tilgang til api.xtramile.no</li>
        </ol>
      </div>

      <div className="border border-gray-200 rounded-lg p-4 my-4">
        <h3 className="text-lg font-semibold text-gray-900">Problem: Ingen attester genereres</h3>
        <p className="text-sm text-gray-600 my-2">Sjekk følgende:</p>
        <ol className="text-sm">
          <li><strong>Kursmapping mangler:</strong> Verifiser at Xtramile-kurset er mappet til en studieplan</li>
          <li><strong>Studieplan ikke godkjent:</strong> Kun godkjente studieplaner kan utstede attester</li>
          <li><strong>Deltaker ikke bestått:</strong> Kun fullførte og beståtte kurs genererer attester</li>
          <li><strong>Allerede utstedt:</strong> Systemet sjekker for duplikater</li>
        </ol>
      </div>

      <div className="border border-gray-200 rounded-lg p-4 my-4">
        <h3 className="text-lg font-semibold text-gray-900">Problem: Kandidat finner ikke attest</h3>
        <p className="text-sm text-gray-600 my-2">Mulige årsaker:</p>
        <ol className="text-sm">
          <li><strong>Annen e-post:</strong> E-posten i Xtramile må matche Kandidatportal-profilen</li>
          <li><strong>Ikke registrert:</strong> Kandidaten må registrere seg i Kandidatportalen</li>
          <li><strong>Synkronisering pågår:</strong> Vent på neste sync-intervall</li>
        </ol>
      </div>

      <h2>API-referanse</h2>
      <p>
        Xtramile-integrasjonen bruker følgende Xtramile API-endepunkter:
      </p>

      <table>
        <thead>
          <tr>
            <th>Endpoint</th>
            <th>Beskrivelse</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td><code>GET /api/v1/statistics</code></td>
            <td>Henter fullføringsdata (hovedkilde)</td>
          </tr>
          <tr>
            <td><code>GET /api/v1/courses</code></td>
            <td>Henter kursinformasjon for mapping</td>
          </tr>
        </tbody>
      </table>

      <h3>Fullføringsdata fra Xtramile</h3>
      <div className="bg-gray-900 text-gray-100 p-4 rounded-lg my-4">
        <pre className="text-sm overflow-x-auto">
{`{
  "courseId": "uuid",
  "courseName": "HMS-kurs for ledere",
  "person": "Ola Nordmann",
  "email": "ola@example.com",
  "completedDate": "2026-03-08T14:30:00Z",
  "isPassed": true,
  "quizScore": 85,
  "quizScoreTotal": 100,
  "certificationId": "uuid"
}`}
        </pre>
      </div>

      <h2>Sikkerhet</h2>
      <ul>
        <li><strong>API-nøkkel:</strong> Lagres kryptert (AES-256-GCM) i databasen</li>
        <li><strong>Dataminimering:</strong> Kun nødvendig data overføres</li>
        <li><strong>Logging:</strong> Alle synkroniseringer logges for revisjon</li>
        <li><strong>Tilgangskontroll:</strong> Kun organisasjonsadministratorer kan konfigurere</li>
      </ul>

      <h2>Støtte</h2>
      <p>
        Ved spørsmål om Xtramile-integrasjonen, kontakt:
      </p>
      <ul>
        <li><strong>Teknisk support:</strong> support@kompetanseforbundet.no</li>
        <li><strong>Xtramile-support:</strong> support@xtramile.no</li>
      </ul>
    </div>
  );
}
